import { Fragment, useEffect, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Download, Lock, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { ChatHeader } from "../components/chat/ChatHeader";
import { Bubble, Typing } from "../components/chat/Bubble";
import { Composer } from "../components/chat/Composer";
import { LiveContent } from "../components/chat/LiveContent";
import { PushBanner } from "../components/chat/PushBanner";
import { OpenChromeBar } from "../components/chat/OpenChromeBar";
import { InstallGuide as InstallHelp } from "../components/support/InstallGuide";
import { useLiveChat } from "../hooks/useLiveChat";
import { useInstall, reportInstalled } from "../lib/install";
import { isValidPhone, msgTime, phoneDigits } from "../lib/live";
import { firstVisitorIndex } from "../lib/hours";
import { logAction, nowTime } from "../lib/chat";
import { useBrand } from "../context/brand";

const OPENING = "Olá, desejo ativar meu App!";

let starting = null;
const startOnce = (chat) => {
  starting = starting || chat.start({}).finally(() => { starting = null; });
  return starting;
};

/** WhatsApp-style sales funnel: anonymous start; the CRM creates the visitor's first message and Bia's reply. */
export default function WaChatPage() {
  const live = useLiveChat();
  const inst = useInstall();
  const brand = useBrand();
  const endRef = useRef(null);
  const opened = useRef(false);
  const [phase, setPhase] = useState("opening");
  const [uploading, setUploading] = useState(false);
  const [local, setLocal] = useState([]);
  const [name, setName] = useState("");
  const [botTyping, setBotTyping] = useState(false);

  const say = (m) => setLocal((prev) => [...prev, { ...m, id: prev.length, time: nowTime() }]);
  const bia = (text, next, delay = 1200) => {
    setBotTyping(true);
    setTimeout(() => { setBotTyping(false); say({ from: "bot", text }); setPhase(next); }, delay);
  };

  // Plan B while the CRM still requires name + phone: Bia asks for them, then the normal start runs.
  const askForData = () => {
    say({ from: "me", text: OPENING });
    bia(`Olá! Aqui é a Bia, da ${brand.name}. Vou ativar seu app agora mesmo. Qual é o seu nome?`, "ask-name", 1500);
  };

  const open = () => {
    setPhase("opening");
    const run = live.chat.hasSession() ? Promise.resolve(live.chat.resume()) : startOnce(live.chat);
    run.then(() => { reportInstalled(); setPhase("live"); })
      .catch((e) => (/inválid/i.test(e?.message || "") ? askForData() : setPhase("error")));
  };

  const submitData = (text) => {
    if (phase === "ask-name") {
      const n = text.slice(0, 60);
      setName(n);
      say({ from: "me", text: n });
      setPhase("busy");
      bia(`Prazer, ${n.split(" ")[0]}! Me passa seu WhatsApp com DDD pra eu deixar tudo certinho no seu cadastro.`, "ask-phone");
      return;
    }
    say({ from: "me", text });
    setPhase("busy");
    if (!isValidPhone(text)) { bia("Não reconheci esse número. Manda com DDD, por exemplo: 11 91234-5678", "ask-phone", 800); return; }
    setBotTyping(true);
    live.chat.start({ name, phone: phoneDigits(text) })
      .then(() => live.chat.send(OPENING))
      .then(() => { reportInstalled(); setLocal((prev) => prev.filter((m) => m.from !== "me" || m.text !== OPENING)); setPhase("live"); })
      .catch((e) => { say({ from: "bot", text: e?.message || "Não consegui iniciar agora. Tente de novo." }); setPhase("ask-phone"); })
      .finally(() => setBotTyping(false));
  };

  useEffect(() => { logAction("view"); }, []);

  useEffect(() => {
    if (!live.chat || opened.current) return undefined;
    opened.current = true;
    open();
    return undefined;
  }, [live.chat]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [phase, live.msgs, live.status?.typing, local, botTyping]);

  const send = (text) => {
    if (phase === "ask-name" || phase === "ask-phone") { submitData(text); return; }
    live.chat.send(text).catch((e) => toast.error(e?.message || "Não foi possível enviar."));
  };
  const upload = (file) => {
    setUploading(true);
    live.chat.upload(file).catch((e) => toast.error(e?.message || "Não foi possível enviar a imagem.")).finally(() => setUploading(false));
  };
  const newChat = () => { live.reset(); setLocal([]); opened.current = true; open(); };

  const isLive = phase === "live";
  const last = live.msgs[live.msgs.length - 1];
  const biaTyping = botTyping || !!live.status?.typing || (isLive && last?.from === "visitor" && !live.msgs.some((m) => m.from !== "visitor"));
  const offlineNote = live.cfg && !live.cfg.online && !live.cfg.ai ? live.cfg.offline_text || "" : "";
  const firstVisitor = firstVisitorIndex(live.msgs);
  const firstReply = live.msgs.findIndex((m) => m.from !== "visitor");

  return (
    <div className="lz-app" data-testid="wa-chat">
      <ChatHeader typing={biaTyping} onNewChat={newChat} onInstall={inst.installed ? undefined : inst.install} cfg={live.cfg}>
        <OpenChromeBar className="mx-3 mb-2" />
      </ChatHeader>
      <main className="lz-chat" data-testid="chat-thread">
        <div className="lz-day">Hoje</div>
        <div className="lz-e2e" data-testid="chat-security-note">
          <Lock size={12} /> As mensagens são protegidas. {brand.official || `Atendimento oficial ${brand.name}.`}
        </div>
        {local.map((m) => (
          <Bubble key={`l-${m.id}`} from={m.from} time={m.time} testId={`chat-message-${m.id}`}>
            <p className="lz-text">{m.text}</p>
          </Bubble>
        ))}
        {live.msgs.map((m, i) => (
          <Fragment key={`live-${m.id}`}>
            <Bubble from={m.from === "visitor" ? "me" : "bot"} time={msgTime(m.at)} testId={`live-message-${m.id}`}>
              <LiveContent m={m} chat={live.chat} />
            </Bubble>
            {i === firstVisitor && offlineNote && (
              <Bubble time={msgTime(m.at)} testId="chat-offline-note"><p className="lz-text">{offlineNote}</p></Bubble>
            )}
            {i === firstReply && !inst.installed && (
              <Bubble time={msgTime(m.at)} testId="chat-install-bubble">
                <p className="lz-text">Quer ser avisado quando responderem? Instale o app de atendimento.</p>
                <button data-testid="chat-install-button" className="lz-install-btn" onClick={inst.install}>
                  <Download size={17} /> Instalar app
                </button>
              </Bubble>
            )}
          </Fragment>
        ))}
        {phase === "error" && (
          <Bubble testId="chat-start-error">
            <p className="lz-text">Não consegui iniciar o atendimento agora. Toque abaixo para tentar de novo.</p>
            <button data-testid="chat-retry-button" className="lz-install-btn" onClick={open}><RotateCcw size={16} /> Tentar de novo</button>
          </Bubble>
        )}
        <AnimatePresence>{(phase === "opening" || biaTyping) && <Typing key="typing" />}</AnimatePresence>
        {isLive && live.msgs.length > 1 && <PushBanner chat={live.chat} cfg={live.cfg} />}
        <div ref={endRef} className="h-24" />
      </main>
      <InstallHelp open={inst.help} onClose={inst.closeHelp} />
      <Composer onSend={send} busy={!isLive && phase !== "ask-name" && phase !== "ask-phone"} uploading={uploading} camera
        tel={phase === "ask-phone"}
        placeholder={phase === "ask-name" ? "Digite seu nome" : phase === "ask-phone" ? "(11) 91234-5678" : "Mensagem"}
        onFile={isLive ? upload : undefined} onTyping={isLive ? () => live.chat.typing() : undefined} />
    </div>
  );
}
